<template>
	<div class="modal-breakdown modal-wrapper">
		<ValidationObserver
			v-slot="{ passes, invalid }"
			tag="div"
			class="modal-breakdown-container"
			@submit.prevent
		>
			<div class="h-full">
				<div>
					<div class="event-sidebar-btn-back font-uppercase">
						{{ $tc('modals.modalBreakdown.markMomentText') }}
					</div>
				</div>

				<div class="flex flex-col justify-between overflow-auto" style="height: calc(100% - 50px);">
					<div class="p-20" style="z-index: 10;">
						<div>
							<div class="small-text text-white font-uppercase mb-20" style="opacity: 0.7">
								{{ $tc('modals.modalBreakdown.chooseTagTypeText') }}
							</div>
							<tag-selector v-model="tagData.type" :sport-type="options.mainEvent.sportType" />
						</div>
					</div>

					<div>
						<div class="p-20">
							<div class="mt-25" style="width: 100px">
								<TextInput
									v-model="tagData.player"
									:label="`${$tc('modals.modalBreakdown.mainPlayerText')} #`"
									name="player"
									class="small-text"
									:rules="{ required: isEdit && tagData.player === '', numeric: true, max: { length: 2 } }"
								/>
							</div>

							<div class="mt-50 mb-50">
								<TextInput
									v-model="tagData.name"
									:placeholder="$tc('labels.tagTitlePlaceholder')"
									:label="$tc('modals.modalBreakdown.nameTheMomentText')"
									name="title"
									class="small-text"
									rules="required|min:3|max:128"
								/>
							</div>
						</div>
						<div class="tags-sections-actions" style="position: relative">
							<transition name="popup-slide-small">
								<div class="bulk-buttons form-group font-uppercase text-truncate">
									<button
										class="button-cancel font-regular"
										:title="$tc('modals.DeleteTagConfirm.cancelButtonText')"
										@click="onClose"
									>
										{{ $tc('modals.DeleteTagConfirm.cancelButtonText') }}
									</button>
									<button
										v-loading="loading"
										class="button-light-blue font-regular"
										:title="$tc('actions.save')"
										:disabled="loading || invalid || isSubmitDisabled"
										@click="passes(onSave)"
									>
										{{ $tc('actions.save') }}
									</button>
								</div>
							</transition>
						</div>
					</div>
				</div>
			</div>
		</ValidationObserver>
	</div>
</template>

<script>
import { mapActions } from 'vuex'

import { ValidationObserver } from 'vee-validate'
import { formatNewEventTagData } from '~/assets/util/events'
import { capitalize } from '~/assets/util/helpers'
import analytics from '~/assets/util/analytics'
import { ModalMixin } from '~/assets/vue/mixins'
import TextInput from '~/components/interface/TextInput'
import TagSelector from '~/components/events/view/breakdown/tags-sections/TagSelector'

export default {
	name: 'ModalBreakdownTags',
	components: {
		ValidationObserver,
		TextInput,
		TagSelector
	},
	mixins: [ModalMixin],
	inject: {
		mainEvent: {
			default: null
		},
		reactivePlayer: {
			default: null
		}
	},
	props: {
		options: {
			type: Object,
			required: true
		}
	},
	data: () => ({
		loading: false,
		tagData: {
			type: 'good',
			name: 'Good',
			player: '',
			startTime: '',
			endTime: ''
		},
		endTagCheckCallback: null
	}),
	computed: {
		isEdit () {
			return Boolean(this.tagData.id)
		},
		isSubmitDisabled () {
			const dataChanges = JSON.stringify(this.options.tag) === JSON.stringify(this.tagData)

			return dataChanges
		},
		playerTimeRangeControlsShown: {
			get () {
				return this.options.player?.ui?.timeRangeControls.isVisible()
			},
			set (value) {
				if (value) {
					this.options.player?.ui?.timeRangeControls.show(true)
				} else {
					this.options.player?.ui?.timeRangeControls.hide()
				}
			}
		}
	},
	watch: {
		'tagData.type': {
			handler (type, oldType) {
				const canUpdateName =
					this.tagData.name.toLowerCase() === oldType?.toLowerCase()

				if (canUpdateName) {
					this.tagData.name = capitalize(type)
				}
			}
		},
		'tagData.player': {
			// allowed to enter only integers from 1 to 99
			handler (num) {
				if (!/^(?:[1-9][0-9]|[0-9])$/.test(num)) {
					this.tagData.player = num.substring(num.length - 1)
				}
			}
		}
	},
	created () {
		if (this.options.tag && this.options.player) {
			this.tagData = Object.assign({}, this.options.tag)
		}

		this.playerTimeRangeControlsShown = true

		this.updateSelectedTimeRange(this.tagData.startTime, this.tagData.endTime)
		this.options.player.on('time-range:drag-end', () => this.updateSelectedTimeRange())
	},
	mounted () {
		const callback = () => {
			if (this.options.player.currentTime >= this.tagData.endTime && !this.options.player.isPaused) {
				this.reactivePlayer.player.currentTime = this.tagData.endTime
				this.options.player.pause()
			}
		}

		if (!this.endTagCheckCallback) {
			this.endTagCheckCallback = callback.bind(this)
			this.options?.player.on('timeupdate', this.endTagCheckCallback)
		}
	},
	beforeDestroy () {
		this.playerTimeRangeControlsShown = false
		this.options?.player.off('timeupdate', this.endTagCheckCallback)
		this.endTagCheckCallback = null
	},
	methods: {
		...mapActions({ analyticsTrack: 'analytics/track' }),

		updateSelectedTimeRange (start, end) {
			if (start || end) {
				this.options.player?.ui?.timeRangeControls?.setTimeRange({ start, end })
			}

			const range = this.options.player?.ui?.timeRangeControls?.getTimeRange()

			this.tagData.startTime = Math.floor(start || range?.start)
			this.tagData.endTime = Math.floor(end || range?.end)
		},
		onClose () {
			this.$emit('close')

			const payload = {
				playMode: this.$route.query.playMode,
				event: this.mainEvent
			}

			this.analyticsTrack({
				event: analytics.AddTagList.event,
				data: analytics.AddTagList.getData(payload)
			})
		},
		async onSave () {
			this.loading = true

			if (this.isEdit) {
				await this.updateTag()
			} else {
				await this.createTag()
			}

			this.loading = false

			const payload = {
				playMode: this.$route.query.playMode,
				event: this.mainEvent,
				tagType: this.tagData.type,
				tagTime: this.tagData.startTime
			}

			this.analyticsTrack({
				event: analytics.AddTagItem.event,
				data: analytics.AddTagItem.getData(payload)
			})
		},

		createTag () {
			const url = `api/v1/events/${this.options.mainEvent.id}/tags`

			return this.$axios.$post(url, this.tagData, { serializeBody: true })
				.then((tag) => {
					const newTag = formatNewEventTagData(this.tagData, this.options.mainEvent.sportType)

					newTag.id = tag.id
					if (newTag.player === '') {
						newTag.player = tag.player
					}

					this.$toast.success(this.$tc('successMessages.tagCreateSuccessMessage'))

					this.onConfirm(newTag)
				})
				.catch((error) => {
					this.$log.error('Failed to create tag', error)
					this.$toast.error(error.errorMessage || this.$tc('errors.tagCreateError'))

					const payload = {
						playMode: this.$route.query.playMode,
						event: this.mainEvent,
						tagType: this.tagData.type,
						tagTime: this.tagData.startTime
					}

					this.analyticsTrack({
						event: analytics.TagCreationFailure.event,
						data: analytics.TagCreationFailure.getData(payload)
					})
				})
		},

		updateTag () {
			const getDiffs = (obj1, obj2) => {
				return Object.keys(obj2).reduce((diff, key) => {
					return obj1[key] === obj2[key] ? diff : { ...diff, [key]: obj2[key] }
				}, {})
			}
			const url = `api/v1/events/${this.options.mainEvent.id}/tags/${this.tagData.id}`
			const payload = getDiffs(this.options.tag, this.tagData)

			if (payload.startTime || payload.startTime === 0 || payload.endTime) {
				payload.startTime = this.tagData.startTime
				payload.endTime = this.tagData.endTime
			}

			return this.$axios.$patch(url, payload, { serializeBody: true })
				.then((tag) => {
					const newTag = formatNewEventTagData(this.tagData, this.options.mainEvent.sportType)

					this.$toast.success(this.$tc('successMessages.tagUpdateSuccessMessage'))

					this.onConfirm(newTag)
				})
				.catch((error) => {
					this.$toast.error(error.errorMessage || this.$tc('errors.tagUpdateError'))
				})
		}
	}
}
</script>

<style lang="scss">
.modal-breakdown {
  width: 100%;
  height: 100%;
  // background-color: $black;
  background-color: #12031d;

  &-container {
    width: 100%;
    height: 100%;
    box-shadow: inset 3px 1px 11px 0 rgba(0, 0, 0, 0.5);
    background-color: rgba(0, 0, 0, 0.3);
  }

  .text-input-label {
    font-size: 12px;
    color: rgba($white, 0.7);
    font-weight: bold;
  }
  .text-input {
    color: $white;
    border-bottom: 2px solid rgba($white, 0.3);
  }
}
</style>
